/**
 * useGasEstimate — estimates gas cost for token deployment
 *
 * Returns estimated cost in wei and formatted display value.
 * Currently uses a static estimate based on typical Clanker V4 deployment gas usage.
 * Gas estimate varies by extension count (Task 2.2).
 * TODO: Use viem publicClient.estimateGas() once deploy params are available.
 */

import type { ExtensionConfig } from "@repo/shared-types";
import { useMemo } from "react";
import { formatEther } from "viem";

import { getNativeCurrencySymbol } from "../../lib/wagmi-config";

export type GasEstimateResult = {
	/** Estimated gas in wei */
	gasWei: bigint;
	/** Formatted display value (e.g., "~0.0025 ETH") */
	formatted: string;
	/** Whether estimation is loading */
	isLoading: boolean;
	/** Whether estimation failed */
	isError: boolean;
	/** Error message if estimation failed */
	errorMessage: string | undefined;
};

type UseGasEstimateParams = {
	/** Whether estimation should run */
	enabled: boolean;
	/** Estimated gas units override */
	estimatedGasUnits?: bigint;
	/** Active extensions — each adds gas overhead */
	extensions?: ReadonlyArray<ExtensionConfig>;
};

/**
 * Base gas estimate for token deployment.
 * Based on typical Clanker V4 deployToken gas usage (~500k-800k gas units).
 */
const BASE_GAS_UNITS = 700_000n;

/** Additional gas per active extension (~50k gas each) */
const GAS_PER_EXTENSION = 50_000n;

/** Conservative gas price for typical L2 (0.05 gwei) */
const ESTIMATED_GAS_PRICE_WEI = 50_000_000n;

export function useGasEstimate({
	enabled,
	estimatedGasUnits,
	extensions,
}: UseGasEstimateParams): GasEstimateResult {
	const extensionCount = extensions?.length ?? 0;
	const gasUnits = estimatedGasUnits ?? BASE_GAS_UNITS + GAS_PER_EXTENSION * BigInt(extensionCount);

	return useMemo(() => {
		if (!enabled) {
			return {
				gasWei: 0n,
				formatted: "—",
				isLoading: false,
				isError: false,
				errorMessage: undefined,
			};
		}
		const estimatedCost = gasUnits * ESTIMATED_GAS_PRICE_WEI;
		return {
			gasWei: estimatedCost,
			formatted: `~${formatEther(estimatedCost)} ${getNativeCurrencySymbol()} (estimated)`,
			isLoading: false,
			isError: false,
			errorMessage: undefined,
		};
	}, [enabled, gasUnits]);
}
